import React from 'react';

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> | null {
        // Suppress permission errors - they're handled by LiveKit PreJoin UI
        const isPermissionError = 
            error.message?.includes("Permission denied") ||
            error.message?.includes("NotAllowedError") ||
            error.message?.includes("PermissionDeniedError") ||
            error.name === "NotAllowedError" ||
            error.name === "PermissionDeniedError";
        
        if (isPermissionError) {
            console.log('Suppressed permission error in ErrorBoundary:', error.message);
            return null; // Don't show error boundary for permission errors
        }
        
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        // Suppress permission errors - they're handled by LiveKit PreJoin UI
        const isPermissionError = 
            error.message?.includes("Permission denied") ||
            error.message?.includes("NotAllowedError") ||
            error.message?.includes("PermissionDeniedError") ||
            error.name === "NotAllowedError" ||
            error.name === "PermissionDeniedError";
        
        if (isPermissionError) {
            console.log('Suppressed permission error in ErrorBoundary:', error.message);
            return; // Don't set error state for permission errors
        }
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div className="error-boundary">
                    <h2>Something went wrong.</h2>
                    <details style={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.error && this.state.error.toString()}
                        <br />
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </details>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
